/**
 * ⚠️ BULLETPROOF AUTH SYSTEM - DO NOT MODIFY ⚠️
 * 
 * This file is part of the immutable auth system.
 * Any modifications will break the login flow.
 * 
 * If your feature needs auth changes, adapt your feature instead.
 * Authentication state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
// Removed authService import - using only AuthCore now
import type { User } from '../types/auth';
import { AuthCore } from '../core/AuthCore';
import { useUserStore } from './userStore';

interface AuthStoreState {
  // Core state - NEVER add more than these 3
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;

  // Core actions - NEVER add complexity here
  login: (provider: 'google' | 'facebook') => Promise<void>;
  logout: () => Promise<void>;
  handleCallback: () => Promise<void>;
  initialize: () => Promise<void>;
}

/**
 * BULLETPROOF AUTH STORE
 * 
 * RULES:
 * 1. NO loading states - they cause complexity
 * 2. NO error handling - let components handle their own errors
 * 3. NO toast notifications - they break the flow
 * 4. NO complex state management - keep it simple
 * 5. ONLY 3 core states: user, isAuthenticated, isInitialized
 * 6. NEVER modify this store for feature requests
 */
export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, _get) => ({
      user: null,
      isAuthenticated: false,
      isInitialized: false,

      // Login - use AuthCore methods
      login: async (provider: 'google' | 'facebook') => {
        // Input validation
        if (!provider || (provider !== 'google' && provider !== 'facebook')) {
          throw new Error('Invalid authentication provider');
        }

        try {
           if (provider === 'google') {
             await AuthCore.getInstance().loginWithGoogle();
           } else if (provider === 'facebook') {
             await AuthCore.getInstance().loginWithFacebook();
           } else {
             throw new Error('Unsupported authentication provider');
           }
           // OAuth flow will redirect, no need to handle response here
        } catch (error) {
          // Let the error bubble up to be handled by components
          throw error;
        }
      },

      // Logout - simplified to use only AuthCore
      logout: async () => {
        try {
          await AuthCore.getInstance().logout();
          // Clear user data on logout
          useUserStore.getState().clearUserDataOnLogout();
          set({ user: null, isAuthenticated: false });
          // Clear persisted auth storage
          localStorage.removeItem('auth-storage');
          window.location.href = '/';
        } catch (error) {
          // Force logout even if service fails
          console.error('Logout process encountered an issue, forcing cleanup');
          useUserStore.getState().clearUserDataOnLogout();
          set({ user: null, isAuthenticated: false });
          localStorage.removeItem('auth-storage');
          window.location.href = '/';
        }
      },

      // Handle OAuth callback - now using AuthCore only
      handleCallback: async () => {
        try {
          // AuthCore already handles OAuth callback automatically
          // Just wait for AuthCore to process and redirect to loading
          const authState = AuthCore.getInstance().getState();
          if (authState.isAuthenticated && authState.user) {
            const user: User = {
              id: authState.user.id,
              email: authState.user.email || '',
              name: authState.user.user_metadata?.full_name || authState.user.email || 'User',
              profile_image: authState.user.user_metadata?.avatar_url
            };
            set({ 
              user, 
              isAuthenticated: true, 
              isInitialized: true 
            });
            // Redirect to loading page for backend connection
            window.location.href = '/loading';
          } else {
            // Auth failed, redirect to home
            window.location.href = '/';
          }
        } catch (error) {
          console.error('Authentication callback failed');
          window.location.href = '/';
        }
      },

      // Initialize - check if user is already logged in
      initialize: async () => {
        try {
          // Get existing AuthCore instance (don't reset to prevent OAuth glitch)
          const authCoreInstance = AuthCore.getInstance();
          await authCoreInstance.initialize();
          
          // Subscribe to AuthCore state changes
          authCoreInstance.subscribe((authState) => {
            let user: User | null = null;
            if (authState.isAuthenticated && authState.user) {
              user = {
                id: authState.user.id,
                email: authState.user.email || '',
                name: authState.user.user_metadata?.full_name || authState.user.email || 'User',
                profile_image: authState.user.user_metadata?.avatar_url
              };
            }
            
            set({ 
              user, 
              isAuthenticated: authState.isAuthenticated
            });
            
            // Credit balance will be handled by useSimpleCredit hook in components
          });
          
          // Get initial auth state from AuthCore
          const authState = authCoreInstance.getState();
          
          // Convert Supabase user to our User format if authenticated
          let user: User | null = null;
          if (authState.isAuthenticated && authState.user) {
            user = {
              id: authState.user.id,
              email: authState.user.email || '',
              name: authState.user.user_metadata?.full_name || authState.user.email || 'User',
              profile_image: authState.user.user_metadata?.avatar_url
            };
          }
          
          set({ 
            user, 
            isAuthenticated: authState.isAuthenticated, 
            isInitialized: true 
          });
        } catch (error) {
          console.error('Auth initialization failed:', error);
          set({ 
            user: null, 
            isAuthenticated: false, 
            isInitialized: true 
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      // Security: Only persist minimal user data to reduce XSS risk
      partialize: (state: AuthStoreState) => ({
        user: state.user ? {
          id: state.user.id,
          email: state.user.email
        } : null,
        isAuthenticated: state.isAuthenticated,
        isInitialized: state.isInitialized
      })
    }
  )
);

// Export a simple hook for components that need auth state
export const useAuth = () => {
  const { user, isAuthenticated, isInitialized } = useAuthStore();
  return { user, isAuthenticated, isInitialized };
};

// Export actions separately to prevent accidental state mutations
export const authActions = {
  login: useAuthStore.getState().login,
  logout: useAuthStore.getState().logout,
  initialize: useAuthStore.getState().initialize,
};